// src/hooks/useWebVitals.ts
import { useEffect, useRef } from 'react';
import { onCLS, onFCP, onINP, onLCP, onTTFB, type Metric } from 'web-vitals';
import { webVitalsAPI } from '../services/api';

/**
 * Captures the five Core Web Vitals (LCP, INP, CLS, FCP, TTFB) using
 * Google's own web-vitals library and reports each to the backend as it
 * finalizes.
 *
 * Important: these metrics are lifecycle-bound to a single *hard* page
 * load, not to React Router's client-side route changes (LCP/CLS/INP all
 * keep observing and only report once, on visibility change/unload). So
 * this hook is mounted exactly once at the app root - mounting it per-route
 * would not give per-route data, just duplicate/incomplete reports for the
 * same underlying page load.
 *
 * The route recorded with each metric is whatever the browser's URL is at
 * the moment the metric fires (which, for most of these, is close to
 * initial load - CLS/INP can fire later, after in-app navigation, in which
 * case the route reflects wherever the user ended up when the page was
 * hidden/unloaded rather than where they started).
 */
export function useWebVitals(): void {
  const hasRegistered = useRef(false);

  useEffect(() => {
    // Defensive guard against double-registration (e.g. hot reload in dev)
    // rather than a StrictMode double-invoke concern - this app doesn't use
    // StrictMode, but registering the same PerformanceObserver twice would
    // double-report every metric, so it's cheap insurance either way.
    if (hasRegistered.current) return;
    hasRegistered.current = true;

    const report = (metric: Metric) => {
      webVitalsAPI
        .recordMetric(
          metric.name,
          metric.value,
          metric.rating,
          window.location.pathname,
          metric.navigationType
        )
        .catch(() => {
          // Best-effort telemetry - a failed report should never surface
          // to the user or affect the page in any way.
        });
    };

    onLCP(report);
    onINP(report);
    onCLS(report);
    onFCP(report);
    onTTFB(report);
  }, []);
}
